// frontend/src/pages/Upgrade.jsx
// Upgrade / pricing page — shown on trial expiry wall and via nav.
// Starter and Growth tiers use Stripe Checkout.
// Enterprise tier shows a mailto contact link.
// Also renders /upgrade/success after Stripe redirect.
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createCheckoutSession } from '../api';

const TIERS = [
  {
    key:      'starter',
    label:    'Starter',
    price:    '$1,200',
    period:   '/month',
    entities: '1 entity',
    target:   'Standard mid-market, below procurement committee',
    priceEnv: 'REACT_APP_STRIPE_PRICE_STARTER',
    cta:      'Upgrade to Starter',
    highlight: false,
  },
  {
    key:      'growth',
    label:    'Growth',
    price:    '$2,800',
    period:   '/month',
    entities: 'Up to 5 entities',
    target:   'Groups, subsidiaries under one APRA licence',
    priceEnv: 'REACT_APP_STRIPE_PRICE_GROWTH',
    cta:      'Upgrade to Growth',
    highlight: true,
  },
  {
    key:      'enterprise',
    label:    'Enterprise',
    price:    'Custom',
    period:   '',
    entities: 'Unlimited entities',
    target:   'Custom regulatory mapping, white-label, API access',
    priceEnv: null,
    cta:      'Contact us',
    highlight: false,
  },
];

const FEATURES = [
  'AI Vendor Register',
  'Control Evidence Locker',
  'Incident Register',
  'Board Pack Generator',
  'Regulatory Library (34 clauses)',
  'User management and role-based access',
  'Automated regulatory monitoring',
  'APRA, FAR, Privacy Act, ASIC, and AUSTRAC coverage',
];

export default function Upgrade({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isSuccess = location.pathname === '/upgrade/success';

  const [loading, setLoading] = useState('');
  const [error,   setError]   = useState('');

  async function handleCheckout(tier) {
    setError('');
    const priceId = process.env[tier.priceEnv];
    if (!priceId) {
      setError('Pricing configuration error. Please contact support.');
      return;
    }
    setLoading(tier.key);
    try {
      const { url } = await createCheckoutSession(priceId);
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoading('');
    }
  }

  if (isSuccess) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f2ed', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: '48px 40px', maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#132e50', marginBottom: 12 }}>
            Subscription activated
          </div>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 32, lineHeight: 1.6 }}>
            Your payment was successful. Full access has been restored to your account.
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ background: '#132e50', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f2ed', padding: '40px 16px' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#b8860b', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
            Flow Advisory Group
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#132e50', marginBottom: 10 }}>
            Choose your plan
          </div>
          <div style={{ fontSize: 14, color: '#6b7280', maxWidth: 480, margin: '0 auto' }}>
            All plans include full access to every platform module. Billed monthly in AUD. Cancel anytime.
          </div>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 16px', color: '#dc2626', fontSize: 13, marginBottom: 24, maxWidth: 600, margin: '0 auto 24px' }}>
            {error}
          </div>
        )}

        {/* Tier cards */}
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 48 }}>
          {TIERS.map(tier => (
            <div
              key={tier.key}
              style={{
                background: '#fff',
                border: tier.highlight ? '2px solid #b8860b' : '1px solid #e5e7eb',
                borderRadius: 10,
                padding: '28px 28px 24px',
                width: 280,
                position: 'relative',
                boxShadow: tier.highlight ? '0 4px 24px rgba(184,134,11,0.12)' : '0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              {tier.highlight && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: '#b8860b', color: '#fff', fontSize: 10, fontWeight: 700,
                  padding: '3px 12px', borderRadius: 10, letterSpacing: 0.5, textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}>
                  Most popular
                </div>
              )}

              <div style={{ fontSize: 16, fontWeight: 700, color: '#132e50', marginBottom: 6 }}>
                {tier.label}
              </div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 28, fontWeight: 700, color: '#132e50' }}>{tier.price}</span>
                <span style={{ fontSize: 13, color: '#6b7280' }}>{tier.period}</span>
              </div>
              <div style={{ fontSize: 12, color: '#b8860b', fontWeight: 600, marginBottom: 12 }}>
                {tier.entities}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 24, lineHeight: 1.5 }}>
                {tier.target}
              </div>

              {tier.key === 'enterprise' ? (
                <a
                  href="mailto:shri@flowadvisorygroup.com?subject=Enterprise%20Enquiry%20%E2%80%94%20AI%20Governance%20Platform"
                  style={{
                    display: 'block', textAlign: 'center',
                    padding: '10px', borderRadius: 6,
                    background: 'none', border: '2px solid #132e50',
                    color: '#132e50', fontSize: 13, fontWeight: 700,
                    textDecoration: 'none',
                  }}
                >
                  Contact us
                </a>
              ) : (
                <button
                  onClick={() => handleCheckout(tier)}
                  disabled={loading === tier.key}
                  style={{
                    width: '100%', padding: '10px',
                    background: tier.highlight ? '#b8860b' : '#132e50',
                    color: '#fff', border: 'none', borderRadius: 6,
                    fontSize: 13, fontWeight: 700,
                    cursor: loading === tier.key ? 'not-allowed' : 'pointer',
                    opacity: loading === tier.key ? 0.7 : 1,
                  }}
                >
                  {loading === tier.key ? 'Redirecting…' : tier.cta}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Feature list */}
        <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', maxWidth: 600, margin: '0 auto', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#132e50', marginBottom: 16 }}>
            All plans include
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
            {FEATURES.map(f => (
              <div key={f} style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#b8860b', fontWeight: 700 }}>✓</span> {f}
              </div>
            ))}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: '#9ca3af' }}>
          Questions? Email <a href="mailto:shri@flowadvisorygroup.com" style={{ color: '#6b7280' }}>shri@flowadvisorygroup.com</a>
        </div>
      </div>
    </div>
  );
}
